import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, FileText, Download, Trash2, LogIn, Upload, AlertCircle, RefreshCw, Database, HardDrive, ShieldCheck, AlertTriangle, Mail, Send, Eye } from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import TopNav from "@/components/TopNav";

function SystemLogs({ user, onLogout }) {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterUser, setFilterUser] = useState("all");
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("logs");

  // Backup states
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // Salud de archivos states
  const [salud, setSalud] = useState(null);
  const [recuperando, setRecuperando] = useState(false);
  const [cargandoRecuperacion, setCargandoRecuperacion] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState(null);
  const recoveryInputRef = useRef(null);

  // MCP states
  const [mcpKeys, setMcpKeys] = useState([]);
  const [mcpAudit, setMcpAudit] = useState([]);
  const [mcpNombre, setMcpNombre] = useState("Claude");
  const [claveNueva, setClaveNueva] = useState(null);
  const [creandoClave, setCreandoClave] = useState(false);
  const [scopeCorreos, setScopeCorreos] = useState(false);

  const [oauthGrants, setOauthGrants] = useState([]);

  const loadMcp = async () => {
    try {
      const [k, a, g] = await Promise.all([
        api.get("/admin/mcp/api-keys"),
        api.get("/admin/mcp/audit-log?limit=100"),
        api.get("/admin/mcp/oauth-conectores"),
      ]);
      setMcpKeys(k.data); setMcpAudit(a.data); setOauthGrants(g.data);
    } catch {}
  };

  const revocarOauth = async (id) => {
    try {
      await api.post(`/admin/mcp/oauth-conectores/${id}/revocar`);
      toast.success("Conector OAuth revocado");
      loadMcp();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error revocando conector");
    }
  };

  const crearClaveMcp = async () => {
    if (!mcpNombre.trim()) { toast.error("Indica el nombre del actor"); return; }
    setCreandoClave(true);
    try {
      const res = await api.post("/admin/mcp/api-keys", { nombre_actor: mcpNombre.trim(), dias_validez: 90, scopes: scopeCorreos ? ["leer", "proponer", "correos"] : ["leer", "proponer"] });
      setClaveNueva(res.data.clave);
      toast.success("Clave creada. Cópiala ahora: no se volverá a mostrar.");
      loadMcp();
    } catch (err) { toast.error(err.response?.data?.detail || "Error creando clave"); }
    finally { setCreandoClave(false); }
  };

  const revocarClave = async (id) => {
    try { await api.post(`/admin/mcp/api-keys/${id}/revocar`); toast.success("Clave revocada"); loadMcp(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error revocando"); }
  };

  // Email & Scheduler states
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [savingCfg, setSavingCfg] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingBackupNow, setSendingBackupNow] = useState(false);
  const [sendingReportNow, setSendingReportNow] = useState(false);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [reportHtml, setReportHtml] = useState("");

  useEffect(() => {
    if (user.actuacion !== "CEO") {
      toast.error("No tienes permisos");
      navigate("/dashboard");
      return;
    }
    loadLogs();
    loadUsers();
  }, [user, navigate, filterType, filterUser]);

  useEffect(() => {
    if (tab === "email" && user.actuacion === "CEO") {
      loadEmailCfg();
      loadEmailLogs();
    }
    if (tab === "backup" && user.actuacion === "CEO") {
      loadSalud();
    }
    if (tab === "mcp" && user.actuacion === "CEO") {
      loadMcp();
    }
  }, [tab, user.actuacion]);

  const loadEmailCfg = async () => {
    try {
      const res = await api.get("/admin/email-config");
      setEmailCfg(res.data);
      setTestRecipient(user.email || "");
    } catch { toast.error("Error cargando configuración"); }
  };
  const loadEmailLogs = async () => {
    try { const res = await api.get("/admin/email-logs"); setEmailLogs(res.data); }
    catch {}
  };

  const saveEmailCfg = async () => {
    setSavingCfg(true);
    try {
      const payload = {
        daily_backup_recipients: typeof emailCfg.daily_backup_recipients === "string"
          ? emailCfg.daily_backup_recipients.split(",").map(s => s.trim()).filter(Boolean)
          : emailCfg.daily_backup_recipients,
        daily_report_recipients: typeof emailCfg.daily_report_recipients === "string"
          ? emailCfg.daily_report_recipients.split(",").map(s => s.trim()).filter(Boolean)
          : emailCfg.daily_report_recipients,
        from_email: emailCfg.from_email,
      };
      const res = await api.put("/admin/email-config", payload);
      setEmailCfg({ ...res.data.config, resend_configured: emailCfg.resend_configured, llm_configured: emailCfg.llm_configured });
      toast.success("Configuración guardada y horarios reprogramados");
    } catch (err) { toast.error(err.response?.data?.detail || "Error guardando"); }
    finally { setSavingCfg(false); }
  };

  const sendTestEmail = async () => {
    if (!testRecipient) { toast.error("Indica un destinatario"); return; }
    setSendingTest(true);
    try {
      const res = await api.post("/admin/email/test", { to: testRecipient });
      if (res.data.success) toast.success(`Email de prueba enviado a ${testRecipient}`);
      else toast.error(`Falló: ${res.data.error || "error desconocido"}`);
      loadEmailLogs();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSendingTest(false); }
  };

  const sendBackupNow = async () => {
    setSendingBackupNow(true);
    try {
      await api.post("/admin/email/run-backup-now");
      toast.success("Backup enviado por email");
      loadEmailLogs();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSendingBackupNow(false); }
  };

  const sendReportNow = async () => {
    setSendingReportNow(true);
    try {
      await api.post("/admin/email/run-report-now");
      toast.success("Reporte enviado por email");
      loadEmailLogs();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSendingReportNow(false); }
  };

  const previewReport = async () => {
    try {
      const res = await api.get("/admin/report/preview");
      setReportHtml(res.data);
      setShowReportPreview(true);
    } catch { toast.error("Error generando vista previa"); }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType !== "all") params.action_type = filterType;
      if (filterUser !== "all") params.user_email = filterUser;
      const response = await api.get("/admin/logs", { params });
      setLogs(response.data);
    } catch { toast.error("Error cargando logs"); }
    finally { setLoading(false); }
  };

  const loadUsers = async () => {
    try { const res = await api.get("/usuarios"); setUsers(res.data); } catch {}
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/backup/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      link.download = `legalstory_backup_${ts}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup descargado exitosamente");
    } catch { toast.error("Error exportando backup"); }
    finally { setExporting(false); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".zip")) { toast.error("Solo archivos ZIP"); return; }
    setSelectedFile(file);
    setShowImportConfirm(true);
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    setImporting(true);
    setShowImportConfirm(false);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const res = await api.post("/admin/backup/import", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(res.data.results);
      toast.success("Backup restaurado exitosamente");
      loadLogs();
    } catch (err) { toast.error(err.response?.data?.detail || "Error importando backup"); }
    finally { setImporting(false); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const loadSalud = async () => {
    try { const res = await api.get("/admin/archivos/salud"); setSalud(res.data); } catch {}
  };

  const handleRecuperarDuplicados = async () => {
    setRecuperando(true);
    try {
      const res = await api.post("/admin/archivos/recuperar-duplicados");
      toast.success(`${res.data.recuperados} archivo(s) recuperados desde copias idénticas`);
      loadSalud();
    } catch (err) { toast.error(err.response?.data?.detail || "Error recuperando duplicados"); }
    finally { setRecuperando(false); }
  };

  const handleInformeFaltantes = async () => {
    try {
      const res = await api.get("/admin/archivos/informe-faltantes", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `archivos_faltantes_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Informe descargado");
    } catch { toast.error("Error descargando informe"); }
  };

  const handleRecoveryZip = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".zip")) { toast.error("Solo archivos ZIP"); return; }
    setCargandoRecuperacion(true);
    setRecoveryResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/admin/archivos/cargar-recuperacion", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setRecoveryResult(res.data);
      toast.success(`${res.data.documentos_actualizados} documento(s) recuperados y protegidos en MongoDB`);
      loadSalud();
    } catch (err) { toast.error(err.response?.data?.detail || "Error cargando ZIP de recuperación"); }
    finally { setCargandoRecuperacion(false); if (recoveryInputRef.current) recoveryInputRef.current.value = ""; }
  };

  const getActionIcon = (t) => ({ login: <LogIn className="h-3.5 w-3.5" />, download: <Download className="h-3.5 w-3.5" />, delete: <Trash2 className="h-3.5 w-3.5" />, upload: <Upload className="h-3.5 w-3.5" />, error: <AlertCircle className="h-3.5 w-3.5" />, backup_export: <HardDrive className="h-3.5 w-3.5" />, backup_import: <Database className="h-3.5 w-3.5" /> }[t] || <FileText className="h-3.5 w-3.5" />);
  const getActionLabel = (t) => ({ login: "Login", download: "Descarga", delete: "Eliminación", upload: "Subida", error: "Error", create: "Creación", update: "Actualización", update_caso: "Edit Caso", backup_export: "Backup", backup_import: "Recovery" }[t] || t);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="system-logs-page">
      <TopNav user={user} onLogout={onLogout} />

      <main className="container mx-auto px-6 py-6 max-w-6xl">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-4 text-xs" style={{ color: "var(--text-muted)" }}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Volver
        </Button>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-5">
          <button onClick={() => setTab("logs")} className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab === "logs" ? "text-white" : ""}`} style={{ background: tab === "logs" ? "var(--primary)" : "var(--surface)", color: tab === "logs" ? "white" : "var(--text-secondary)" }} data-testid="tab-logs">
            <FileText className="h-3.5 w-3.5 inline mr-1.5" />Logs de Auditoría
          </button>
          <button onClick={() => setTab("backup")} className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab === "backup" ? "text-white" : ""}`} style={{ background: tab === "backup" ? "var(--primary)" : "var(--surface)", color: tab === "backup" ? "white" : "var(--text-secondary)" }} data-testid="tab-backup">
            <Database className="h-3.5 w-3.5 inline mr-1.5" />Backup & Recovery
          </button>
          <button onClick={() => setTab("email")} className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab === "email" ? "text-white" : ""}`} style={{ background: tab === "email" ? "var(--primary)" : "var(--surface)", color: tab === "email" ? "white" : "var(--text-secondary)" }} data-testid="tab-email">
            <Mail className="h-3.5 w-3.5 inline mr-1.5" />Email & Programación
          </button>
          <button onClick={() => setTab("mcp")} className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab === "mcp" ? "text-white" : ""}`} style={{ background: tab === "mcp" ? "var(--primary)" : "var(--surface)", color: tab === "mcp" ? "white" : "var(--text-secondary)" }} data-testid="tab-mcp">
            MCP / Claves IA
          </button>
        </div>

        {/* TAB: MCP */}
        {tab === "mcp" && (
          <div className="space-y-4" data-testid="mcp-panel">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Claves de API para IA (MCP)</CardTitle>
                <CardDescription className="text-xs">Permiten a Claude leer el expediente y proponer hechos (con tu aprobación). La clave se muestra UNA sola vez. Guía: docs/MCP.md</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-3">
                  <input value={mcpNombre} onChange={e => setMcpNombre(e.target.value)} placeholder="Nombre del actor (ej. Claude)" className="text-xs border rounded-lg px-3 h-9 flex-1" data-testid="mcp-nombre-input" />
                  <Button onClick={crearClaveMcp} disabled={creandoClave} className="ls-btn-primary h-9 text-xs" data-testid="mcp-crear-clave-btn">Crear clave (90 días)</Button>
                </div>
                <label className="flex items-center gap-2 text-xs mb-3" data-testid="mcp-scope-correos-label">
                  <input type="checkbox" checked={scopeCorreos} onChange={e => setScopeCorreos(e.target.checked)} data-testid="mcp-scope-correos-check" />
                  Incluir scope <b>correos</b> (acceso de lectura al Data Lake de correos Vault)
                </label>
                {claveNueva && (
                  <div className="p-3 rounded-lg bg-green-50 border border-green-300 mb-3" data-testid="mcp-clave-nueva">
                    <p className="text-xs font-bold text-green-800">Clave creada — cópiala AHORA (no se volverá a mostrar):</p>
                    <code className="text-xs break-all select-all">{claveNueva}</code>
                  </div>
                )}
                <div className="space-y-2">
                  {mcpKeys.map(k => (
                    <div key={k.id} className="flex items-center justify-between p-2 rounded-lg text-xs" style={{ background: "var(--bg)" }} data-testid={`mcp-key-${k.id}`}>
                      <div>
                        <span className="font-semibold">{k.nombre_actor}</span> · <code>{k.key_prefix}…</code>
                        <span className="ml-2" style={{ color: "var(--text-muted)" }}>expira {String(k.expira_en).slice(0, 10)} · último uso: {k.ultimo_uso ? String(k.ultimo_uso).slice(0, 16) : "nunca"}</span>
                        {k.revocada && <span className="ml-2 text-red-600 font-semibold">REVOCADA</span>}
                      </div>
                      {!k.revocada && <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-600 border-red-300" onClick={() => revocarClave(k.id)} data-testid={`mcp-revocar-${k.id}`}>Revocar</Button>}
                    </div>
                  ))}
                  {mcpKeys.length === 0 && <p className="text-xs" style={{ color: "var(--text-muted)" }}>No hay claves creadas.</p>}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Conectores OAuth (app de Claude)</CardTitle>
                <CardDescription className="text-xs">Conectores autorizados vía OAuth 2.1 desde claude.ai. Mismas restricciones que las claves: leer + proponer, sin edición ni borrado.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2" data-testid="oauth-grants-list">
                  {oauthGrants.map(g => (
                    <div key={g.id} className="flex items-center justify-between p-2 rounded-lg text-xs" style={{ background: "var(--bg)" }} data-testid={`oauth-grant-${g.id}`}>
                      <div>
                        <span className="font-semibold">{g.client_name}</span>
                        <span className="ml-2" style={{ color: "var(--text-muted)" }}>
                          scopes: {(g.scopes || []).join(", ")} · autorizado por {g.autorizado_por} el {String(g.creado).slice(0, 10)} · último uso: {g.ultimo_uso ? String(g.ultimo_uso).slice(0, 16) : "nunca"}
                        </span>
                        {g.revocado && <span className="ml-2 text-red-600 font-semibold">REVOCADO</span>}
                      </div>
                      {!g.revocado && <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-600 border-red-300" onClick={() => revocarOauth(g.id)} data-testid={`oauth-revocar-${g.id}`}>Revocar</Button>}
                    </div>
                  ))}
                  {oauthGrants.length === 0 && <p className="text-xs" style={{ color: "var(--text-muted)" }}>Ningún conector OAuth autorizado aún.</p>}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Auditoría MCP (append-only)</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="mcp-audit-list">
                  {mcpAudit.map(a => (
                    <div key={a.id} className="text-[11px] flex gap-2 py-1 border-b" style={{ borderColor: "var(--border)" }}>
                      <span style={{ color: "var(--text-muted)" }}>{String(a.timestamp).slice(0, 19).replace("T", " ")}</span>
                      <span className="font-semibold">{a.nombre_actor || "?"}</span>
                      <span>{a.herramienta}</span>
                      <span className={a.resultado === "ok" ? "text-green-600" : "text-red-600"}>{a.resultado}</span>
                      <span className="truncate" style={{ color: "var(--text-muted)" }}>{a.parametros_resumen}</span>
                    </div>
                  ))}
                  {mcpAudit.length === 0 && <p className="text-xs" style={{ color: "var(--text-muted)" }}>Sin actividad MCP aún.</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* TAB: LOGS */}
        {tab === "logs" && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div><CardTitle className="text-base">Logs del Sistema</CardTitle><CardDescription className="text-xs">Registro de acciones</CardDescription></div>
                <div className="flex gap-2">
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="login">Login</SelectItem>
                      <SelectItem value="download">Descarga</SelectItem>
                      <SelectItem value="delete">Eliminación</SelectItem>
                      <SelectItem value="upload">Subida</SelectItem>
                      <SelectItem value="update_caso">Edit Caso</SelectItem>
                      <SelectItem value="backup_export">Backup</SelectItem>
                      <SelectItem value="backup_import">Recovery</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={loadLogs}><RefreshCw className="h-3 w-3 mr-1" />Actualizar</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-sm" style={{ color: "var(--text-muted)" }}>Cargando...</div>
              ) : logs.length === 0 ? (
                <div className="text-center py-8 text-sm" style={{ color: "var(--text-muted)" }}>No hay logs</div>
              ) : (
                <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
                  {logs.map((log, i) => (
                    <div key={log.id || i} className="flex items-center gap-3 px-3 py-2 rounded-lg" style={{ background: i % 2 === 0 ? "var(--bg)" : "transparent" }}>
                      <div className="shrink-0" style={{ color: "var(--text-muted)" }}>{getActionIcon(log.action_type)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs truncate" style={{ color: "var(--text)" }}>{log.description}</p>
                        <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>{log.user_email}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px] shrink-0">{getActionLabel(log.action_type)}</Badge>
                      <span className="text-[10px] shrink-0 tabular-nums" style={{ color: "var(--text-muted)" }}>
                        {new Date(log.timestamp).toLocaleString("es-CL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* TAB: BACKUP */}
        {tab === "backup" && (
          <div className="space-y-5">
            {/* Export */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><HardDrive className="h-4 w-4" style={{ color: "var(--primary)" }} /> Exportar Backup Completo</CardTitle>
                <CardDescription className="text-xs">Descarga un archivo ZIP con toda la base de datos, hechos, documentos, PDFs y archivos adjuntos.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 rounded-lg mb-4" style={{ background: "var(--bg)" }}>
                  <p className="text-xs font-medium mb-2" style={{ color: "var(--text)" }}>El backup incluye:</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]" style={{ color: "var(--text-secondary)" }}>
                    <span>Casos y datos judiciales</span>
                    <span>Hechos y timeline</span>
                    <span>Documentos y evidencias</span>
                    <span>Archivos PDF adjuntos</span>
                    <span>Fotos y archivos</span>
                    <span>Valorización económica</span>
                    <span>Fases procesales</span>
                    <span>Usuarios y abogados</span>
                    <span>Empresas y demandados</span>
                    <span>Logs de auditoría</span>
                    <span>Data Lake completo</span>
                    <span>Configuración de fases</span>
                  </div>
                </div>
                <Button onClick={handleExport} disabled={exporting} className="ls-btn-primary w-full h-10" data-testid="export-backup-btn">
                  {exporting ? (
                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Generando backup...</>
                  ) : (
                    <><Download className="h-4 w-4 mr-2" />Descargar Backup Completo (.zip)</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Import */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4" style={{ color: "var(--accent-amber)" }} /> Recovery (Restaurar Backup)</CardTitle>
                <CardDescription className="text-xs">Sube un archivo ZIP de backup previamente exportado para restaurar datos en caso de desastre.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-3 rounded-lg mb-4 flex items-start gap-2" style={{ background: "rgba(234, 179, 8, 0.08)", border: "1px solid rgba(234, 179, 8, 0.2)" }}>
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--accent-amber)" }} />
                  <p className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                    <strong>Importante:</strong> La restauración actualizará los datos existentes y agregará los que no existan. Los datos actuales no se eliminan, se fusionan con el backup.
                  </p>
                </div>
                <input ref={fileInputRef} type="file" accept=".zip" onChange={handleFileSelect} className="hidden" data-testid="import-file-input" />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing} className="w-full h-10 text-sm" data-testid="import-backup-btn">
                  {importing ? (
                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Restaurando...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" />Seleccionar archivo ZIP de backup</>
                  )}
                </Button>

                {/* Import Results */}
                {importResult && (
                  <div className="mt-4 p-4 rounded-lg" style={{ background: "var(--bg)" }} data-testid="import-results">
                    <p className="text-xs font-bold mb-2" style={{ color: "var(--primary)" }}>Restauración completada</p>
                    <div className="space-y-1">
                      {importResult.collections_restored?.map((c, i) => (
                        <div key={i} className="flex justify-between text-[11px]">
                          <span style={{ color: "var(--text-secondary)" }}>{c.name}</span>
                          <span className="font-medium" style={{ color: "var(--text)" }}>{c.docs} registros</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-[11px] pt-1" style={{ borderTop: "1px solid var(--border)" }}>
                        <span style={{ color: "var(--text-secondary)" }}>Archivos restaurados</span>
                        <span className="font-medium" style={{ color: "var(--text)" }}>{importResult.files_restored}</span>
                      </div>
                      {importResult.errors?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-[10px] text-red-500 font-medium">Errores:</p>
                          {importResult.errors.map((e, i) => <p key={i} className="text-[10px] text-red-400">{e}</p>)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Salud de Archivos & Recuperación */}
            <Card data-testid="salud-archivos-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4" style={{ color: "var(--primary)" }} /> Salud de Archivos (MongoDB)</CardTitle>
                <CardDescription className="text-xs">Estado de los archivos adjuntos: protegidos en la base de datos vs. perdidos. Sube aquí el ZIP de recuperación con los PDF rescatados de tu correo.</CardDescription>
              </CardHeader>
              <CardContent>
                {salud ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="p-3 rounded-lg text-center" style={{ background: "var(--bg)" }}>
                      <p className="text-xl font-bold" style={{ color: "var(--text)" }} data-testid="salud-total">{salud.total_documentos}</p>
                      <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>Documentos totales</p>
                    </div>
                    <div className="p-3 rounded-lg text-center" style={{ background: "rgba(34,197,94,0.08)" }}>
                      <p className="text-xl font-bold text-green-600" data-testid="salud-protegidos">{salud.protegidos}</p>
                      <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>Protegidos en MongoDB</p>
                    </div>
                    <div className="p-3 rounded-lg text-center" style={{ background: "rgba(234,179,8,0.08)" }}>
                      <p className="text-xl font-bold text-amber-600" data-testid="salud-recuperables">{salud.recuperables_copia}</p>
                      <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>Recuperables (copia)</p>
                    </div>
                    <div className="p-3 rounded-lg text-center" style={{ background: "rgba(239,68,68,0.08)" }}>
                      <p className="text-xl font-bold text-red-600" data-testid="salud-perdidos">{salud.solo_texto + salud.sin_nada}</p>
                      <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>Perdidos (resubir)</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>Cargando estado...</p>
                )}

                <div className="grid md:grid-cols-3 gap-2">
                  <Button variant="outline" onClick={handleRecuperarDuplicados} disabled={recuperando} className="h-10 text-xs" data-testid="recuperar-duplicados-btn">
                    {recuperando ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                    Recuperar copias idénticas
                  </Button>
                  <Button variant="outline" onClick={handleInformeFaltantes} className="h-10 text-xs" data-testid="informe-faltantes-btn">
                    <Download className="h-4 w-4 mr-1.5" />Informe de faltantes (CSV)
                  </Button>
                  <Button onClick={() => recoveryInputRef.current?.click()} disabled={cargandoRecuperacion} className="ls-btn-primary h-10 text-xs" data-testid="cargar-recuperacion-btn">
                    {cargandoRecuperacion ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                    Subir ZIP de recuperación
                  </Button>
                  <input ref={recoveryInputRef} type="file" accept=".zip" onChange={handleRecoveryZip} className="hidden" data-testid="recovery-zip-input" />
                </div>
                <p className="text-[10px] mt-2" style={{ color: "var(--text-muted)" }}>
                  El ZIP de recuperación asocia cada PDF por su nombre EXACTO a todos los casos y hechos que lo referencian, y lo protege en MongoDB. Si el ZIP pesa más de 80 MB, divídelo en partes y súbelas una a una.
                </p>

                {recoveryResult && (
                  <div className="mt-4 p-4 rounded-lg" style={{ background: "var(--bg)" }} data-testid="recovery-results">
                    <p className="text-xs font-bold mb-2" style={{ color: "var(--primary)" }}>Resultado de la recuperación</p>
                    <div className="space-y-1 text-[11px]" style={{ color: "var(--text-secondary)" }}>
                      <div className="flex justify-between"><span>Archivos en el ZIP</span><span className="font-medium">{recoveryResult.archivos_en_zip}</span></div>
                      <div className="flex justify-between"><span>Documentos recuperados y protegidos</span><span className="font-medium text-green-600">{recoveryResult.documentos_actualizados}</span></div>
                      <div className="flex justify-between"><span>Ya estaban protegidos</span><span className="font-medium">{recoveryResult.ya_protegidos}</span></div>
                      <div className="flex justify-between"><span>Sin coincidencia de nombre</span><span className="font-medium text-red-500">{recoveryResult.sin_match?.length || 0}</span></div>
                      {recoveryResult.sin_match?.length > 0 && (
                        <div className="mt-2 max-h-32 overflow-y-auto">
                          <p className="text-[10px] font-medium text-red-500">Nombres sin match (renómbralos según el informe y vuelve a subir):</p>
                          {recoveryResult.sin_match.map((n, i) => <p key={i} className="text-[10px] text-red-400 truncate">{n}</p>)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        {/* TAB: EMAIL & PROGRAMACIÓN */}
        {tab === "email" && (
          <div className="space-y-5">
            {!emailCfg ? (
              <Card><CardContent className="py-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>Cargando configuración...</CardContent></Card>
            ) : (
              <>
                {/* Status */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" style={{ color: "var(--primary)" }} /> Estado de la integración</CardTitle>
                    <CardDescription className="text-xs">Conexión Resend (email) y LLM (sugerencias IA).</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg flex items-center gap-3" style={{ background: "var(--bg)" }}>
                        <div className="h-2 w-2 rounded-full" style={{ background: emailCfg.resend_configured ? "#22c55e" : "#ef4444" }} />
                        <div className="flex-1">
                          <p className="text-xs font-medium">Resend</p>
                          <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>{emailCfg.resend_configured ? "API Key configurada" : "Falta RESEND_API_KEY"}</p>
                        </div>
                      </div>
                      <div className="p-3 rounded-lg flex items-center gap-3" style={{ background: "var(--bg)" }}>
                        <div className="h-2 w-2 rounded-full" style={{ background: emailCfg.llm_configured ? "#22c55e" : "#ef4444" }} />
                        <div className="flex-1">
                          <p className="text-xs font-medium">LLM (Sugerencias IA)</p>
                          <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>{emailCfg.llm_configured ? "Emergent LLM Key activa" : "No configurada"}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 p-3 rounded-lg" style={{ background: "rgba(234, 179, 8, 0.08)", border: "1px solid rgba(234, 179, 8, 0.2)" }}>
                      <p className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                        <strong>Remitente:</strong> Si usas un dominio propio (ej. <code>no-reply@legal.marioandres.cl</code>), debes verificarlo en <a href="https://resend.com/domains" target="_blank" rel="noreferrer" style={{ color: "var(--primary)" }}>resend.com/domains</a>. Mientras tanto, puedes usar <code>onboarding@resend.dev</code> y verificar destinatarios uno por uno en Resend.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Test send */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2"><Send className="h-4 w-4" style={{ color: "var(--primary)" }} /> Probar envío</CardTitle>
                    <CardDescription className="text-xs">Envía un email de prueba para validar que Resend funciona.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      <Input value={testRecipient} onChange={(e) => setTestRecipient(e.target.value)} placeholder="correo@ejemplo.cl" className="text-sm" data-testid="email-test-recipient" />
                      <Button onClick={sendTestEmail} disabled={sendingTest} className="ls-btn-primary" data-testid="email-test-send">
                        {sendingTest ? <RefreshCw className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Enviar prueba</>}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Aviso: todo manual */}
                <div className="p-3 rounded-lg text-xs" style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", color: "var(--text-secondary)" }} data-testid="email-automaticos-desactivados">
                  <strong>Envíos automáticos desactivados.</strong> Ningún backup ni reporte se envía solo: todo es manual mediante los botones "Enviar ahora". Así no se gastan tokens de IA ni correos sin tu orden.
                </div>

                {/* Backup manual */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2"><HardDrive className="h-4 w-4" style={{ color: "var(--primary)" }} /> Backup por correo (manual)</CardTitle>
                    <CardDescription className="text-xs">Envía el ZIP de la BD + archivos a los correos indicados, solo cuando pulses el botón.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div><Label className="text-xs">Destinatarios (separados por coma)</Label>
                      <Input value={Array.isArray(emailCfg.daily_backup_recipients) ? emailCfg.daily_backup_recipients.join(", ") : emailCfg.daily_backup_recipients} onChange={(e) => setEmailCfg({ ...emailCfg, daily_backup_recipients: e.target.value })} className="text-sm" data-testid="cfg-backup-recipients" /></div>
                    <Button variant="outline" size="sm" onClick={sendBackupNow} disabled={sendingBackupNow} className="text-xs" data-testid="email-backup-now">
                      {sendingBackupNow ? <><RefreshCw className="h-3 w-3 mr-1 animate-spin" />Enviando...</> : <><Send className="h-3 w-3 mr-1" /> Enviar backup ahora</>}
                    </Button>
                  </CardContent>
                </Card>

                {/* Reporte manual */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" style={{ color: "var(--primary)" }} /> Reporte con sugerencias IA (manual)</CardTitle>
                    <CardDescription className="text-xs">Resumen del día por caso + tips IA. Consume tokens de IA solo al pulsar "Enviar" o "Vista previa".</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div><Label className="text-xs">Destinatarios</Label>
                      <Input value={Array.isArray(emailCfg.daily_report_recipients) ? emailCfg.daily_report_recipients.join(", ") : emailCfg.daily_report_recipients} onChange={(e) => setEmailCfg({ ...emailCfg, daily_report_recipients: e.target.value })} className="text-sm" data-testid="cfg-report-recipients" /></div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={previewReport} className="text-xs" data-testid="email-report-preview">
                        <Eye className="h-3 w-3 mr-1" /> Vista previa
                      </Button>
                      <Button variant="outline" size="sm" onClick={sendReportNow} disabled={sendingReportNow} className="text-xs" data-testid="email-report-now">
                        {sendingReportNow ? <><RefreshCw className="h-3 w-3 mr-1 animate-spin" />Enviando...</> : <><Send className="h-3 w-3 mr-1" /> Enviar reporte ahora</>}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Remitente */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Email remitente (FROM)</CardTitle>
                    <CardDescription className="text-xs">Dirección que aparecerá como remitente. El dominio debe estar verificado en Resend.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Input value={emailCfg.from_email || ""} onChange={(e) => setEmailCfg({ ...emailCfg, from_email: e.target.value })} placeholder="no-reply@tudominio.cl" className="text-sm" data-testid="cfg-from-email" />
                  </CardContent>
                </Card>

                <Button onClick={saveEmailCfg} disabled={savingCfg} className="ls-btn-primary w-full h-10" data-testid="email-save-cfg">
                  {savingCfg ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Guardando...</> : "Guardar configuración y reprogramar horarios"}
                </Button>

                {/* Logs de email */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Últimos envíos</CardTitle>
                    <CardDescription className="text-xs">Historial de emails enviados por el sistema.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {emailLogs.length === 0 ? (
                      <p className="text-xs text-center py-6" style={{ color: "var(--text-muted)" }}>Aún no se han enviado emails.</p>
                    ) : (
                      <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="email-logs-list">
                        {emailLogs.map((log, i) => (
                          <div key={log.id || i} className="flex items-center gap-3 px-3 py-2 rounded-lg" style={{ background: i % 2 === 0 ? "var(--bg)" : "transparent" }}>
                            <div className="h-2 w-2 rounded-full shrink-0" style={{ background: log.success ? "#22c55e" : "#ef4444" }} />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs truncate" style={{ color: "var(--text)" }}>{log.subject}</p>
                              <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>{Array.isArray(log.to) ? log.to.join(", ") : log.to} · {log.kind}</p>
                              {log.error && <p className="text-[10px] text-red-500 truncate">{log.error}</p>}
                            </div>
                            <span className="text-[10px] shrink-0 tabular-nums" style={{ color: "var(--text-muted)" }}>{new Date(log.timestamp).toLocaleString("es-CL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}
      </main>

      {/* Preview Report Dialog */}
      <Dialog open={showReportPreview} onOpenChange={setShowReportPreview}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vista previa del reporte</DialogTitle>
            <DialogDescription>Así llegará el email a los destinatarios.</DialogDescription>
          </DialogHeader>
          <div className="border rounded" dangerouslySetInnerHTML={{ __html: reportHtml }} />
        </DialogContent>
      </Dialog>

      {/* Confirm Import Dialog */}
      <Dialog open={showImportConfirm} onOpenChange={setShowImportConfirm}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5" style={{ color: "var(--accent-amber)" }} /> Confirmar Recovery</DialogTitle>
            <DialogDescription>¿Estás seguro de restaurar el backup? Los datos existentes se fusionarán con los del archivo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg text-xs" style={{ background: "var(--bg)", color: "var(--text-secondary)" }}>
              <strong>Archivo:</strong> {selectedFile?.name} ({(selectedFile?.size / 1024 / 1024).toFixed(1)} MB)
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setShowImportConfirm(false); setSelectedFile(null); }}>Cancelar</Button>
              <Button className="flex-1 ls-btn-primary" onClick={handleImport} data-testid="confirm-import-btn">
                <ShieldCheck className="h-4 w-4 mr-1" /> Restaurar Backup
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SystemLogs;
